import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase/config';
import { 
  ArrowRight, 
  Users, 
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  Heart
} from 'lucide-react';
import { cn } from '../utils';
import LanguageSelector from './LanguageSelector';

interface LandingPageLayoutProps {
  children: React.ReactNode;
  currentPage?: string;
}

const LandingPageLayout: React.FC<LandingPageLayoutProps> = ({ children, currentPage = '' }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [loading, setLoading] = useState(false);

  // 스크롤 감지
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      setLoading(true);
      await auth.signOut();
    } catch (e: any) {
      console.error('로그아웃 실패:', e);
      alert(`로그아웃 실패: ${e.message || '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };

  // 홈 섹션으로 안전하게 이동 (다른 페이지에서도 동작)
  const goToHomeSection = (sectionId: string) => {
    const hash = `#${sectionId}`;
    if (window.location.pathname !== '/') {
      // 라우터를 통해 홈으로 이동 + 해시 포함 (SPA 내 전환)
      navigate(`/${hash}`);
      return;
    }
    // 현재가 홈이면 스무스 스크롤
    const el = document.getElementById(sectionId);
    if (el) {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch {
        el.scrollIntoView();
      }
      // 주소 표시줄 해시 동기화
      if (window.location.hash !== hash) {
        history.replaceState(null, '', hash);
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Header Bar */}
      <div className="hidden md:block lux-ink border-b border-white/10 text-white py-2">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Quick Links & Social Media Icons - Hidden on mobile */}
            <div className="hidden md:flex items-center space-x-4">
              <a href="https://twitter.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#c8a45e] transition-colors" aria-label="Twitter">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href="https://facebook.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#c8a45e] transition-colors" aria-label="Facebook">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="https://linkedin.com/company/scanvcf" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#c8a45e] transition-colors" aria-label="LinkedIn">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="https://discord.gg/scanvcf" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#c8a45e] transition-colors" aria-label="Discord">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.781h-1.68V5.526h1.68v1.681zm-1.68 9.781c-1.297 0-2.448-.49-3.323-1.297-.928-.875-1.418-2.026-1.418-3.323s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297z"/>
                </svg>
              </a>
              <a href="https://youtube.com/@scanvcf" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#c8a45e] transition-colors" aria-label="YouTube">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>

            {/* User Actions */}
            <div className="flex items-center space-x-2 md:space-x-4">
              <LanguageSelector variant="button" />
              
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-sm font-medium"
                  >
                    <LayoutDashboard className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">{t('navigation.dashboard', 'Dashboard')}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-sm font-medium"
                    disabled={loading}
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    <span>{t('auth.logout', 'Logout')}</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-sm font-medium"
                  >
                    <Users className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{t('authForm.title.signUp')}</span>
                  <span className="sm:hidden">{t('authForm.title.signUp')}</span>
                  </Link>
                  <Link
                    to="/login"
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-sm font-medium"
                  >
                    <ArrowRight className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{t('authForm.title.signIn', 'Sign In')}</span>
                  <span className="sm:hidden">{t('authForm.title.signIn', 'Sign In')}</span>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Top Header - 언어 선택 및 사용자 액션 */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-[60] lux-ink border-b border-white/10 text-white">
        <div className="mx-auto max-w-[1280px] px-4 py-2">
          <div className="flex items-center justify-between">
            {/* 언어 선택 */}
            <div className="flex items-center">
              <LanguageSelector variant="mobile" />
            </div>
            
            {/* 사용자 액션 */}
            <div className="flex items-center space-x-2">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-xs font-medium"
                  >
                    <LayoutDashboard className="h-3 w-3 mr-1" />
                    <span className="hidden sm:inline">{t('navigation.dashboard', 'Dashboard')}</span>
                    <span className="sm:hidden">{t('navigation.dashboard', 'Dashboard')}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-xs font-medium"
                    disabled={loading}
                  >
                    <LogOut className="h-3 w-3 mr-1" />
                    <span>{t('auth.logout', 'Logout')}</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-xs font-medium"
                  >
                    <Users className="h-3 w-3 mr-1" />
                    <span className="hidden sm:inline">{t('auth.register', 'Register')}</span>
                    <span className="sm:hidden">{t('auth.register', 'Sign Up')}</span>
                  </Link>
                  <Link
                    to="/login"
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-xs font-medium"
                  >
                    <ArrowRight className="h-3 w-3 mr-1" />
                  <span className="hidden sm:inline">{t('authForm.title.signIn', 'Sign In')}</span>
                  <span className="sm:hidden">{t('authForm.title.signIn', 'Sign In')}</span>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={cn(
        "fixed left-0 right-0 z-40 transition-all duration-300",
        "top-12 md:top-12", // 모바일과 데스크톱 모두 top-12 (상단 헤더 아래)
        isScrolled ? "bg-white/95 backdrop-blur-md border-b border-gray-200/70 shadow-sm" : "bg-white md:bg-transparent"
      )}>
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <img
                src="/scanvcf_icon_transparent_pack/scanvcf_icon_square_transparent_32.png"
                alt="ScanVCF"
                className="w-8 h-8 rounded-lg"
              />
              <span className="text-xl font-bold text-gray-900">ScanVCF</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => goToHomeSection('home')} className={cn(
                "transition-colors",
                currentPage === 'home' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
              )}>
                {t('navigation.home', 'Home')}
              </button>
              <button onClick={() => goToHomeSection('benefits')} className={cn(
                "transition-colors",
                currentPage === 'benefits' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
              )}>
                {t('navigation.benefits', 'Benefits')}
              </button>
              <Link to="/features" className={cn(
                "transition-colors",
                currentPage === 'features' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
              )}>
                {t('navigation.features', 'Features')}
              </Link>
              <button onClick={() => goToHomeSection('how-it-works')} className={cn(
                "transition-colors",
                currentPage === 'how-it-works' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
              )}>
                {t('navigation.howItWorks', 'How It Works')}
              </button>
              <button onClick={() => goToHomeSection('pricing')} className={cn(
                "transition-colors",
                currentPage === 'pricing' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
              )}>
                {t('navigation.pricing', 'Pricing')}
              </button>
              <Link to="/contact" className={cn(
                "transition-colors",
                currentPage === 'contact' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
              )}>
                {t('navigation.contact', 'Contact')}
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200">
              <div className="px-4 py-2 space-y-1">
                <button onClick={() => { goToHomeSection('home'); setIsMobileMenuOpen(false); }} className={cn(
                  "block px-3 py-2 transition-colors",
                  currentPage === 'home' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
                )}>
                  {t('navigation.home', 'Home')}
                </button>
                <button onClick={() => { goToHomeSection('benefits'); setIsMobileMenuOpen(false); }} className={cn(
                  "block px-3 py-2 transition-colors",
                  currentPage === 'benefits' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
                )}>
                  {t('navigation.benefits', 'Benefits')}
                </button>
                <Link to="/features" className={cn(
                  "block px-3 py-2 transition-colors",
                  currentPage === 'features' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
                )} onClick={() => setIsMobileMenuOpen(false)}>
                  {t('navigation.features', 'Features')}
                </Link>
                <button onClick={() => { goToHomeSection('how-it-works'); setIsMobileMenuOpen(false); }} className={cn(
                  "block px-3 py-2 transition-colors",
                  currentPage === 'how-it-works' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
                )}>
                  {t('navigation.howItWorks', 'How It Works')}
                </button>
                <button onClick={() => { goToHomeSection('pricing'); setIsMobileMenuOpen(false); }} className={cn(
                  "block px-3 py-2 transition-colors",
                  currentPage === 'pricing' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
                )}>
                  {t('navigation.pricing', 'Pricing')}
                </button>
                <Link to="/contact" className={cn(
                  "block px-3 py-2 transition-colors",
                  currentPage === 'contact' ? "text-[#96742f] font-semibold" : "text-gray-600 hover:text-gray-900"
                )} onClick={() => setIsMobileMenuOpen(false)}>
                  {t('navigation.contact', 'Contact')}
                </Link>
                {/* Mobile User Actions */}
                <div className="px-3 py-2 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <LanguageSelector variant="mobile" />
                  </div>
                  {isAuthenticated ? (
                    <div className="space-y-2">
                      <Link
                        to="/dashboard"
                        className="flex items-center justify-center w-full px-4 py-2 text-gray-600 hover:text-primary-600 font-medium border border-gray-300 rounded-lg"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        {t('navigation.dashboard', 'Dashboard')}
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center justify-center w-full px-4 py-2 text-gray-600 hover:text-primary-600 font-medium border border-gray-300 rounded-lg"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('navigation.settings', 'Settings')}
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center justify-center w-full px-4 py-2 text-gray-600 hover:text-primary-600 font-medium border border-gray-300 rounded-lg"
                        disabled={loading}
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        {t('auth.logout', 'Logout')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Link
                        to="/login"
                        className="flex items-center justify-center w-full px-4 py-2 text-gray-600 hover:text-primary-600 font-medium border border-gray-300 rounded-lg"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <ArrowRight className="h-4 w-4 mr-2" />
                        {t('authForm.title.signIn', 'Sign In')}
                      </Link>
                  <Link
                        to="/register"
                        className="flex items-center justify-center w-full px-4 py-2 lux-ink text-white hover:opacity-90 font-medium rounded-lg"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Users className="h-4 w-4 mr-2" />
                    {t('authForm.title.signUp', 'Sign Up')}
                      </Link>
                    </div>
                  )}
                </div>
                
                {/* Android App Banner */}
                <div className="px-3 py-2 border-t border-gray-200">
                  <div className="lux-ink rounded-lg p-4 text-white border border-[#c8a45e]/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <svg className="h-8 w-8 mr-3" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                        </svg>
                        <div>
                          <h3 className="font-semibold text-sm">Android 앱 다운로드</h3>
                          <p className="text-white/60 text-xs">모바일에서 더 편리하게</p>
                        </div>
                      </div>
                      <a
                        href="https://play.google.com/store/apps/details?id=com.scanvcf.app"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white text-primary-600 px-3 py-1 rounded-md text-xs font-medium hover:bg-primary-50 transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        다운로드
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-20 md:pt-24">
        {children}
      </main>

      {/* Footer */}
      <footer className="lux-ink border-t border-white/10 text-white py-16">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <img
                  src="/scanvcf_icon_transparent_pack/scanvcf_icon_square_transparent_32.png"
                  alt="ScanVCF"
                  className="w-8 h-8 rounded-lg"
                />
                <span className="text-xl font-bold">ScanVCF</span>
              </div>
              <p className="text-gray-400 mb-6">
                {t('landing.footer.description', 'Create and share professional digital business cards with QR codes.')}
              </p>
              <div className="flex space-x-4">
                <a href="https://twitter.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#c8a45e] transition-colors">
                  <span className="sr-only">Twitter</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a href="https://facebook.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#c8a45e] transition-colors">
                  <span className="sr-only">Facebook</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="https://linkedin.com/company/scanvcf" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#c8a45e] transition-colors">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href="https://discord.gg/scanvcf" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#c8a45e] transition-colors">
                  <span className="sr-only">Discord</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.781h-1.68V5.526h1.68v1.681zm-1.68 9.781c-1.297 0-2.448-.49-3.323-1.297-.928-.875-1.418-2.026-1.418-3.323s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297z"/>
                  </svg>
                </a>
                <a href="https://youtube.com/@scanvcf" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#c8a45e] transition-colors">
                  <span className="sr-only">YouTube</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.product', 'Product')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/features" className="hover:text-white transition-colors">{t('landing.footer.features', 'Features')}</Link></li>
                <li>
                  <Link
                    to="/#pricing"
                    className="hover:text-white transition-colors"
                    onClick={(e) => { e.preventDefault(); window.location.href = '/#pricing'; }}
                  >
                    {t('landing.footer.pricing', 'Pricing')}
                  </Link>
                </li>
                <li><a href="https://play.google.com/store/apps/details?id=com.scanvcf.app" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{t('landing.footer.download', 'Download')}</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.support', 'Support')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/help" className="hover:text-white transition-colors">{t('landing.footer.help', 'Help Center')}</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">{t('landing.footer.contact', 'Contact Us')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.legal', 'Legal')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/privacy.html" className="hover:text-white transition-colors">{t('landing.footer.privacy', 'Privacy Policy')}</a></li>
                <li><a href="/terms.html" className="hover:text-white transition-colors">{t('landing.footer.terms', 'Terms of Service')}</a></li>
                <li><a href="/delete-account.html" className="hover:text-white transition-colors">{t('landing.footer.delete', 'Delete Account')}</a></li>
                <li><a href="/refund-policy.html" className="hover:text-white transition-colors">{t('landing.footer.cancellation', 'Subscription Cancellation/Refund')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                {t('landing.footer.copyright', '© 2025 ScanVCF. All rights reserved.')}
              </p>
              <div className="flex items-center space-x-4 mt-4 md:mt-0">
                <span className="text-gray-400 text-sm">
                  Made with <Heart className="inline w-4 h-4 text-red-500" /> by <a href="https://t2design.com.au/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">T2DESIGN</a>
                </span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageLayout;
